import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AnalyticsService } from './api/services';
import { TrackEventDto } from './api/models';
import type { CoreProvider } from './core.provider';

/** Event vocabulary accepted by the API. Kept in step with ANALYTICS_EVENTS on the backend. */
export type AnalyticsEvent = TrackEventDto['event'];

const ANONYMOUS_ID_KEY = 'hai-analytics-anonymous-id';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

/**
 * Generates a v4 uuid.
 *
 * `crypto.randomUUID` is only exposed in secure contexts, which the Cordova WebView is not
 * guaranteed to be, so it cannot be relied on alone — the fallback builds the same value out of
 * `getRandomValues`, which has no such restriction.
 */
function randomUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch {}
  }

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * In-house product analytics, replacing Google Tag Manager (web/Android) and Firebase Analytics
 * (iOS). Events go to our own API and land in the analytics_events table.
 *
 * Named a Provider rather than a Service to avoid colliding with the generated API client's
 * AnalyticsService, which it wraps.
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsProvider {

  private core?: CoreProvider;
  /**
   * Events fired before the app finished booting. They are held rather than sent so they do not
   * lose the platform and version context, which is only known once CoreProvider has initialised.
   */
  private pending: { event: AnalyticsEvent; properties?: Record<string, unknown>; checkoutAttemptId?: string; occurredAt: string }[] = [];

  constructor(private readonly api: AnalyticsService) {}

  /**
   * Called from CoreProvider.init(), matching the initCore pattern of the other services. Taking
   * the core this way rather than injecting it keeps the dependency one-directional — CoreProvider
   * injects this provider, so injecting it back would be a cycle.
   */
  initCore(core: CoreProvider) {
    this.core = core;
    this.captureUtmParams();

    const queued = this.pending;
    this.pending = [];
    for (const item of queued) this.send(item.event, item.properties, item.occurredAt, item.checkoutAttemptId);
  }

  /**
   * Stable per-device identifier, so activity before a user registers can be attributed and later
   * joined to the account.
   *
   * Held in localStorage rather than the app's StorageService because that one is asynchronous,
   * and the id has to be available synchronously for the very first event after launch.
   */
  get anonymousId(): string | undefined {
    try {
      let id = localStorage.getItem(ANONYMOUS_ID_KEY);
      if (!id) {
        id = randomUuid();
        localStorage.setItem(ANONYMOUS_ID_KEY, id);
      }
      return id;
    } catch {
      // Private mode or a locked-down WebView. Anonymous attribution is lost, events still flow.
      return undefined;
    }
  }

  /**
   * Records an event. Never throws and never blocks the caller: a failed analytics call must not
   * surface in the UI or interrupt what the user was doing.
   */
  track(event: AnalyticsEvent, properties?: Record<string, unknown>, checkoutAttemptId?: string) {
    const occurredAt = new Date().toISOString();

    if (!this.core) {
      this.pending.push({ event, properties, checkoutAttemptId, occurredAt });
      return;
    }

    this.send(event, properties, occurredAt, checkoutAttemptId);
  }

  /** Fresh id for one upgrade attempt, tying `checkout_started` to the server-side conversion. */
  newCheckoutAttemptId(): string {
    return randomUuid();
  }

  private send(event: AnalyticsEvent, properties: Record<string, unknown> | undefined, occurredAt: string, checkoutAttemptId?: string) {
    const body: TrackEventDto = {
      event,
      occurredAt,
      anonymousId: this.anonymousId,
      platform: this.core?.isAndroid ? 'android' : this.core?.isIOS ? 'ios' : this.core?.isWeb ? 'web' : 'unknown',
      appVersion: this.core?.version,
      env: environment.name,
      checkoutAttemptId,
      ...this.getUtmParams(),
      properties: properties ?? {},
    };

    // Deliberately not awaited: analytics is never on the critical path of a user action. The
    // bearer token, when there is one, is attached by the auth interceptor, which is what lets the
    // API attribute the event to an account.
    firstValueFrom(this.api.analyticsControllerTrackEvent({ body })).catch(error => {
      console.debug('Analytics event not delivered', event, error);
    });
  }

  /**
   * Captures campaign parameters once per session so every subsequent event carries the attribution
   * of the link the user arrived through, not just the landing page view.
   */
  private captureUtmParams() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (!urlParams.get('utm_source')) return;

      for (const key of UTM_KEYS) sessionStorage.setItem(key, urlParams.get(key) || '');
    } catch (error) {
      console.debug('Could not capture UTM params', error);
    }
  }

  private getUtmParams(): Pick<TrackEventDto, 'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmTerm' | 'utmContent'> {
    try {
      return {
        utmSource: sessionStorage.getItem('utm_source') || undefined,
        utmMedium: sessionStorage.getItem('utm_medium') || undefined,
        utmCampaign: sessionStorage.getItem('utm_campaign') || undefined,
        utmTerm: sessionStorage.getItem('utm_term') || undefined,
        utmContent: sessionStorage.getItem('utm_content') || undefined,
      };
    } catch {
      return {};
    }
  }
}
